"use client";

import { useEffect, useMemo, useState } from "react";
import { toast as sonnerToast } from "sonner";
import { Check, LoaderCircle } from "lucide-react";
import {
  billingOfferDefinitions,
  type BillingCatalogOffer,
  type BillingInterval,
} from "@reelmagic/domain";
import { useBillingPlan } from "./BillingPlanProvider";
import { AppModal } from "@/components/ui/AppModal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { reportCaughtError } from "@/lib/sentry/report-caught-error";
import { useFirebaseAuth } from "../auth/AuthProvider";
import {
  createCustomerPortalLink,
  getBillingCatalog,
  getBillingSetupChecklist,
  startStripeExtensionCheckout,
} from "@/lib/billing/stripe-extension";
import {
  formatBillingPrice,
  PLAN_AI_VIDEO_REEL_COPY,
  PLAN_FEATURES,
  PLAN_STANDARD_REEL_COPY,
} from "./billing-plan-copy";

function formatUpgradeOfferPrice(
  offer: BillingCatalogOffer,
  price: BillingCatalogOffer["prices"][number],
) {
  const unitAmount =
    offer.interval === "year" && price.unitAmount !== null
      ? price.unitAmount / 12
      : price.unitAmount;

  return formatBillingPrice(unitAmount, price.currency);
}

const upgradePlanModalContentClassName =
  "max-h-[min(90dvh,780px)] max-w-[min(calc(100%-2rem),74rem)] overflow-y-auto";
const upgradePlanModalBodyClassName = "gap-5";
const upgradePlanCycleRowClassName =
  "flex flex-wrap items-center justify-center gap-3";
const upgradePlanGridClassName =
  "grid grid-cols-1 gap-3 min-[900px]:grid-cols-3";
const upgradePlanCardClassName =
  "relative grid min-h-full gap-4 rounded-lg border border-[var(--rm-border)] bg-white/[0.035] p-4 shadow-none";
const upgradePlanCardFeaturedClassName =
  "border-[rgba(225,23,39,0.35)] bg-white/[0.05]";
const upgradePlanCardStackClassName = "grid gap-2.5";
const upgradePlanCardTitleRowClassName =
  "flex items-start justify-between gap-3";
const upgradePlanCardTitleClassName =
  "m-0 font-display text-[1.45rem] tracking-[-0.05em]";
const upgradePlanBadgeClassName =
  "inline-flex min-h-[24px] items-center justify-center rounded-full bg-[rgba(225,23,39,0.12)] px-2 text-[0.65rem] font-extrabold uppercase tracking-[0.08em] text-[#ff7f88]";
const upgradePlanPriceRowClassName = "flex items-end gap-2";
const upgradePlanPriceClassName =
  "font-display text-[2.25rem] tracking-[-0.07em]";
const upgradePlanMutedClassName =
  "text-[0.88rem] leading-relaxed text-[var(--rm-text-muted)]";
const upgradePlanEstimateClassName =
  "text-[0.95rem] font-bold text-[#ff4d59]";
const upgradePlanFeaturesClassName = "m-0 grid list-none gap-2.5 p-0";
const upgradePlanFeatureClassName =
  "flex items-start gap-2.5 text-[0.88rem] leading-relaxed text-[var(--rm-text-soft)]";
const upgradePlanFeatureMarkerClassName =
  "mt-0.5 inline-flex size-4 shrink-0 items-center justify-center text-[#ff4d59]";
const upgradePlanFooterClassName = "mt-auto grid gap-2 text-center";
const upgradePlanSkeletonCardClassName = "relative overflow-hidden";
const upgradePlanSkeletonClassName =
  "relative block overflow-hidden rounded-full bg-white/[0.07] after:absolute after:inset-0 after:translate-x-[-100%] after:bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.1)_48%,transparent_100%)] after:content-[''] after:animate-[billingSkeletonShimmer_1.35s_ease-in-out_infinite]";
const upgradePlanSkeletonTitleClassName = "h-[18px] w-[42%]";
const upgradePlanSkeletonPriceClassName = "h-[52px] w-[64%]";
const upgradePlanSkeletonShortClassName = "h-4 w-[36%]";
const upgradePlanSkeletonLineClassName = "h-[14px] w-full";
const upgradePlanSkeletonLineShortClassName = "w-[82%]";
const upgradePlanSkeletonButtonClassName = "mt-auto h-10 w-full rounded-xl";

function buildSuccessUrl() {
  const url = new URL(window.location.href);
  url.searchParams.set("checkout", "success");
  return url.toString();
}

export function UpgradePlanModal({
  description,
  isOpen,
  onClose,
  title = "Choose a plan to upgrade",
}: {
  description?: string;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}) {
  const { user } = useFirebaseAuth();
  const [offers, setOffers] = useState<BillingCatalogOffer[]>(
    billingOfferDefinitions.map((offer) => ({
      ...offer,
      prices: [],
      productId: null,
      synced: false,
    })),
  );
  const [hasLoadedCatalog, setHasLoadedCatalog] = useState(
    () => !getBillingSetupChecklist().stripeExtensionConfigured,
  );
  const billing = useBillingPlan();
  const { planId: currentPlanId, hasActiveSubscription } = billing;
  const [billingCycleSelection, setBillingCycle] = useState<BillingInterval | null>(null);
  const billingCycle = billingCycleSelection ?? billing.billingInterval ?? "year";
  const [checkoutOfferKey, setCheckoutOfferKey] = useState<string | null>(null);
  const [isPortalPending, setIsPortalPending] = useState(false);
  const checklist = useMemo(() => getBillingSetupChecklist(), []);
  const subscriptionOffers = useMemo(
    () =>
      offers.filter(
        (offer) =>
          offer.kind === "subscription" && offer.interval === billingCycle,
      ),
    [billingCycle, offers],
  );
  const isCatalogLoading = billing.status === "loading" ||
    (checklist.stripeExtensionConfigured && !hasLoadedCatalog);
  const isBillingBusy = checkoutOfferKey !== null || isPortalPending;

  useEffect(() => {
    if (!isOpen || !checklist.stripeExtensionConfigured) {
      return;
    }

    let isMounted = true;

    void getBillingCatalog()
      .then((result) => {
        if (!isMounted) {
          return;
        }
        setOffers(result.offers);
        setHasLoadedCatalog(true);
      })
      .catch((error: unknown) => {
        if (isMounted) {
          reportCaughtError(error, { operation: "billing.modal_load_catalog" });
          setHasLoadedCatalog(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [checklist.stripeExtensionConfigured, isOpen]);

  async function startCheckout(offer: BillingCatalogOffer) {
    if (billing.status !== "ready") return;
    const price = offer.prices[0];

    if (!user) {
      sonnerToast.error("Sign in first so checkout can create a billing session.");
      return;
    }

    if (hasActiveSubscription) {
      await openBillingPortal();
      return;
    }

    if (!price) {
      sonnerToast.error(`Pricing has not synced yet for ${offer.label}.`);
      return;
    }

    setCheckoutOfferKey(offer.key);

    try {
      const session = await startStripeExtensionCheckout({
        cancelUrl: window.location.href,
        mode: "subscription",
        offerKey: offer.key,
        priceId: price.id,
        successUrl: buildSuccessUrl(),
      });
      window.location.assign(session.url);
    } catch (error) {
      reportCaughtError(error, {
        context: { offerKey: offer.key },
        operation: "billing.modal_start_checkout",
      });
      sonnerToast.error(
        error instanceof Error
          ? error.message
          : "Unable to create checkout session.",
      );
    } finally {
      setCheckoutOfferKey(null);
    }
  }

  async function openBillingPortal() {
    if (billing.status !== "ready" || !hasActiveSubscription) return;
    setIsPortalPending(true);

    try {
      const portalLink = await createCustomerPortalLink(window.location.href);

      if (!portalLink) {
        sonnerToast.error("Billing portal link returned empty.");
        return;
      }

      window.location.assign(portalLink);
    } catch (error) {
      reportCaughtError(error, { operation: "billing.modal_open_portal" });
      sonnerToast.error(
        error instanceof Error
          ? error.message
          : "Unable to open billing portal.",
      );
    } finally {
      setIsPortalPending(false);
    }
  }

  return (
    <AppModal
      body={
        <div className="grid gap-5">
          {billing.status === "unavailable" ? <p role="alert" className="text-sm text-destructive">Your plan could not load. Reload the page to try again.</p> : null}
          <div className={upgradePlanCycleRowClassName}>
            <Tabs
              onValueChange={(value) => {
                if (value === "month" || value === "year") {
                  setBillingCycle(value);
                }
              }}
              value={billingCycle}
            >
              <TabsList
                aria-label="Billing cycle"
                className="border border-[var(--rm-border)] bg-white/[0.04] text-[var(--rm-text-muted)] shadow-none"
              >
                <TabsTrigger className="min-w-[6.5rem]" value="month">
                  Monthly
                </TabsTrigger>
                <TabsTrigger className="min-w-[6.5rem]" value="year">
                  Yearly
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className={upgradePlanGridClassName}>
            {isCatalogLoading
              ? Array.from({ length: 3 }, (_, index) => (
                  <UpgradePlanSkeleton key={index} />
                ))
              : subscriptionOffers.map((offer) => {
                  const price = offer.prices[0];
                  const paidPlanId =
                    offer.planId && offer.planId !== "free" ? offer.planId : null;
                  const features = paidPlanId ? PLAN_FEATURES[paidPlanId] : [];
                  const planBadge = offer.featured ? "Most Popular" : offer.badge;
                  const isCurrentPlan =
                    hasActiveSubscription && offer.planId === currentPlanId;

                  return (
                    <article
                      className={cn(
                        upgradePlanCardClassName,
                        offer.featured && upgradePlanCardFeaturedClassName,
                      )}
                      key={offer.key}
                    >
                      <div className={upgradePlanCardStackClassName}>
                        <div className={upgradePlanCardTitleRowClassName}>
                          <h4 className={upgradePlanCardTitleClassName}>
                            {offer.label}
                          </h4>
                          {planBadge ? (
                            <span className={upgradePlanBadgeClassName}>
                              {planBadge}
                            </span>
                          ) : null}
                        </div>

                        <div className={upgradePlanPriceRowClassName}>
                          <strong className={upgradePlanPriceClassName}>
                            {price
                              ? formatUpgradeOfferPrice(offer, price)
                              : "Pending"}
                          </strong>
                          <span className={upgradePlanMutedClassName}>
                            {offer.interval === "year"
                              ? "/month, billed annually"
                              : "/month"}
                          </span>
                        </div>

                        {paidPlanId ? (
                          <div className="grid gap-1">
                            <span className={upgradePlanEstimateClassName}>
                              {PLAN_STANDARD_REEL_COPY[paidPlanId]} / mo
                            </span>
                            <span className={upgradePlanEstimateClassName}>
                              or {PLAN_AI_VIDEO_REEL_COPY[paidPlanId]} / mo
                            </span>
                          </div>
                        ) : null}
                      </div>

                      <ul
                        aria-label="What's included"
                        className={upgradePlanFeaturesClassName}
                      >
                        {features.map((feature) => (
                          <li
                            className={upgradePlanFeatureClassName}
                            key={feature}
                          >
                            <span
                              aria-hidden="true"
                              className={upgradePlanFeatureMarkerClassName}
                            >
                              <Check className="size-3.5" />
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <div className={upgradePlanFooterClassName}>
                        <Button
                          aria-busy={
                            checkoutOfferKey === offer.key ||
                            (hasActiveSubscription && isPortalPending)
                          }
                          disabled={
                            isCurrentPlan ||
                            isCatalogLoading ||
                            billing.status !== "ready" ||
                            isBillingBusy ||
                            (!hasActiveSubscription && !price)
                          }
                          onClick={() => void startCheckout(offer)}
                          type="button"
                          variant={offer.featured ? "aura" : "default"}
                        >
                          {isCurrentPlan ? (
                            "Current plan"
                          ) : checkoutOfferKey === offer.key ||
                            (hasActiveSubscription && isPortalPending) ? (
                            <>
                              <LoaderCircle
                                aria-hidden="true"
                                className="size-4 animate-spin"
                              />
                              Redirecting...
                            </>
                          ) : hasActiveSubscription ? (
                            "Change plan"
                          ) : (
                            "Subscribe"
                          )}
                        </Button>
                        <span className={upgradePlanMutedClassName}>
                          Cancel anytime
                        </span>
                      </div>
                    </article>
                  );
                })}
          </div>

          <p className={cn("m-0", upgradePlanMutedClassName)}>
            *Estimated monthly output varies by reel length, model choice, and
            generation settings. Top up with credit packs whenever you need more
            room to keep working on your reels.
          </p>

          {hasActiveSubscription ? (
            <Button
              disabled={isBillingBusy}
              onClick={() => void openBillingPortal()}
              type="button"
              variant="secondary"
            >
              {isPortalPending ? (
                <LoaderCircle
                  aria-hidden="true"
                  className="size-4 animate-spin"
                />
              ) : null}
              Manage subscription
            </Button>
          ) : null}
        </div>
      }
      bodyClassName={upgradePlanModalBodyClassName}
      contentClassName={upgradePlanModalContentClassName}
      description={description}
      isDismissDisabled={isBillingBusy}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      {null}
    </AppModal>
  );
}

function UpgradePlanSkeleton() {
  return (
    <article
      aria-hidden="true"
      className={cn(upgradePlanCardClassName, upgradePlanSkeletonCardClassName)}
    >
      <div className={upgradePlanCardStackClassName}>
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonTitleClassName,
          )}
        />
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonPriceClassName,
          )}
        />
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonLineClassName,
          )}
        />
      </div>
      <div className={upgradePlanCardStackClassName}>
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonShortClassName,
          )}
        />
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonLineClassName,
          )}
        />
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonLineClassName,
            upgradePlanSkeletonLineShortClassName,
          )}
        />
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonLineClassName,
          )}
        />
        <span
          className={cn(
            upgradePlanSkeletonClassName,
            upgradePlanSkeletonLineClassName,
            upgradePlanSkeletonLineShortClassName,
          )}
        />
      </div>
      <span
        className={cn(
          upgradePlanSkeletonClassName,
          upgradePlanSkeletonButtonClassName,
        )}
      />
    </article>
  );
}
